import { ref, reactive } from 'vue'
import { defineStore } from 'pinia'
import { toast } from 'vue-sonner'
import aplosApi from '@/services/aplosApi'
import { storeRegistry } from '@/store/storeRegistry'
import { extractApiErrorMessage } from '@/utils'
import type {
  MetricsSnapshot,
  MetricsSnapshotResponse,
  AggregatedCounts,
  MetricsQueryParams,
} from '@/models/MetricsSnapshot'

export const useMetricsSnapshotStore = defineStore('metricsSnapshot', () => {
  // State per scope
  const crossTenantData = ref<MetricsSnapshotResponse | null>(null)
  const perTenantData = ref<Record<string, MetricsSnapshotResponse>>({})
  const perUserData = ref<Record<string, MetricsSnapshotResponse>>({})

  // Loading state per scope
  const loading = reactive({
    crossTenant: false,
    perTenant: {} as Record<string, boolean>,
    perUser: {} as Record<string, boolean>,
  })

  // Error state
  const error = ref<string | null>(null)

  // --- Actions ---

  async function fetchCrossTenantMetrics(params?: MetricsQueryParams) {
    loading.crossTenant = true
    error.value = null
    try {
      const response = await aplosApi.getMetricsSnapshots(params)
      crossTenantData.value = response.data as MetricsSnapshotResponse
    } catch (e: any) {
      const message = extractApiErrorMessage(e) ?? 'Failed to fetch cross-tenant metrics'
      error.value = message
      toast.error(message)
    } finally {
      loading.crossTenant = false
    }
  }

  async function fetchTenantMetrics(tenantId: string, params?: MetricsQueryParams) {
    loading.perTenant[tenantId] = true
    error.value = null
    try {
      const response = await aplosApi.getTenantMetricsSnapshots(tenantId, params)
      perTenantData.value[tenantId] = response.data as MetricsSnapshotResponse
    } catch (e: any) {
      const message = extractApiErrorMessage(e) ?? `Failed to fetch metrics for tenant ${tenantId}`
      error.value = message
      toast.error(message)
    } finally {
      loading.perTenant[tenantId] = false
    }
  }

  async function fetchUserMetrics(tenantId: string, userId: string, params?: MetricsQueryParams) {
    const key = `${tenantId}:${userId}`
    loading.perUser[key] = true
    error.value = null
    try {
      const response = await aplosApi.getUserMetricsSnapshots(tenantId, userId, params)
      perUserData.value[key] = response.data as MetricsSnapshotResponse
    } catch (e: any) {
      const message = extractApiErrorMessage(e) ?? `Failed to fetch metrics for user ${userId}`
      error.value = message
      toast.error(message)
    } finally {
      loading.perUser[key] = false
    }
  }

  // --- Cache invalidation ---

  function invalidateScope(scope: 'crossTenant' | 'perTenant' | 'perUser', key?: string) {
    if (scope === 'crossTenant') {
      crossTenantData.value = null
    } else if (scope === 'perTenant') {
      if (key) {
        delete perTenantData.value[key]
      } else {
        perTenantData.value = {}
      }
    } else if (scope === 'perUser') {
      if (key) {
        delete perUserData.value[key]
      } else {
        perUserData.value = {}
      }
    }
  }

  function invalidateAll() {
    crossTenantData.value = null
    perTenantData.value = {}
    perUserData.value = {}
    error.value = null
  }

  // --- Aggregation ---

  function aggregateCounts(snapshots: MetricsSnapshot[]): AggregatedCounts {
    return snapshots.reduce<AggregatedCounts>(
      (acc, snapshot) => {
        acc.runningCount += snapshot.runningCount
        acc.pendingCount += snapshot.pendingCount
        acc.succeededCount += snapshot.succeededCount
        acc.failedCount += snapshot.failedCount
        acc.cancelledCount += snapshot.cancelledCount
        acc.throttledCount += snapshot.throttledCount
        acc.rejectedCount += snapshot.rejectedCount
        acc.totalCount += snapshot.totalCount
        return acc
      },
      {
        runningCount: 0,
        pendingCount: 0,
        succeededCount: 0,
        failedCount: 0,
        cancelledCount: 0,
        throttledCount: 0,
        rejectedCount: 0,
        totalCount: 0,
      },
    )
  }

  function resetStore() {
    invalidateAll()
  }

  storeRegistry.register('metricsSnapshot', resetStore)

  return {
    crossTenantData,
    perTenantData,
    perUserData,
    loading,
    error,
    fetchCrossTenantMetrics,
    fetchTenantMetrics,
    fetchUserMetrics,
    invalidateScope,
    invalidateAll,
    aggregateCounts,
    resetStore,
  }
})
